// Authentication Context
// Save as: ~/cfo-dashboard/frontend/src/contexts/AuthContext.js

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const [authError, setAuthError] = useState('');
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const retryVerification = () => setVerificationAttempt(attempt => attempt + 1);

  useEffect(() => {
    let disposed = false;
    if (!token) {
      setLoading(false);
      setAuthError('');
      return;
    }
    setLoading(true);
    setAuthError('');
    axios.get('/api/auth/verify', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      if (!disposed) setUser(response.data.user);
    }).catch(error => {
      if (disposed) return;
      if ([401, 403].includes(error.response?.status)) {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
      } else {
        // A temporary server/network failure does not invalidate a credential.
        setUser(null);
        setAuthError('We couldn’t verify your connection. Please try again.');
      }
    }).finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [token, verificationAttempt]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const value = {
    user,
    token,
    loading,
    authError,
    retryVerification,
    login,
    logout,
    isAuthenticated: !!user,
    isSuperadmin: user?.role === 'superadmin',
    isAdmin: user?.role === 'admin',
    isStaff: user?.role === 'admin' || user?.role === 'superadmin',
    isSalesperson: user?.role === 'salesperson',
    isMarketing: user?.role === 'marketing',
    isSalesAdmin: user?.role === 'sales_admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
